/**
 * @description This file contains methods to trigger adobe analytics events
 * Uses shared analytics client from @pfizer/shared/analytics
 */

import {
  trackFormWorkflow as baseTrackFormWorkflow,
  trackFormInput as baseTrackFormInput,
  trackContentLiked as baseTrackContentLiked,
  trackContentDisliked as baseTrackContentDisliked,
  trackCustomLink as baseTrackCustomLink,
  trackDownload as baseTrackDownload,
} from '@pfizer/shared/analytics/client';

import type { CustomLinkType } from '@pfizer/shared/analytics/types';

/**
 * @method trackFormWorkFlow
 * @description sets custom form workflow tracking
 * @param {string} formName (form name) [name of the form]
 * @param {string} action (action) [action of the form - initiated, step, completed, submitted, error]
 * @param {string} stepNumber (step number) [for pagination, set to 1 if only one page]
 * @param {string} pageLoad (page load) [string variable, set to true if action takes place during page load or false for click]
 * @param {string} errorMessage (Error Message) [optional parameter, do not set if no errors, or set to error, or could be a concatenated list of error messages]
 */
export const trackFormWorkFlow = baseTrackFormWorkflow;

/**
 * @method trackFormInput
 * @description sets custom form input tracking
 * @param {string} formName (form name) [name of the form]
 * @param {string} stepNumber (step number) [for pagination, set to 1 if only one page]
 * @param {string} formQuestion (form input question) [question statement of form field]
 * @param {string} formAnswer (form input answer) [answer statement of form field, set to empty string for any PII info]
 * @param {string} questionIndex (index of question) [question number in form]
 */
export const trackFormInput = baseTrackFormInput;

/**
 * @method trackContentLiked
 * @description This method tracks the contents liked by the user
 * @param {String} contentTitle (Content Title) [title of content user liked]
 * @param {String} contentType (Content Type) [type of content, could be page, article, document, video, webinar, etc.]
 * @param {String} contentPlacement (Content Placement) [Content placement, or location on page, could be tile, rail, banner, etc. Optional]
 */
export const trackContentLiked = baseTrackContentLiked;

/**
 * @method trackContentDisLiked
 * @description This method tracks the contents disliked by the user
 * @param {String} contentTitle (Content Title) [title of content user disliked]
 * @param {String} contentType (Content Type) [type of content, could be page, article, document, video, webinar, etc.]
 * @param {String} contentPlacement (Content Placement) [Content placement, or location on page, could be tile, rail, banner, etc. Optional]
 */
export const trackContentDisLiked = baseTrackContentDisliked;

/**
 * @method trackCustomLinkUsage
 * @description This method tracks a custom link(feature) usage
 * @param {string} featureName (feature name) [feature like that is being tracked]
 * @param {string} linkType (link type) [type of the link being tracked (internal or external)]
 */
export const trackCustomLinkUsage = (
  featureName: string,
  linkType: CustomLinkType
) => {
  baseTrackCustomLink(featureName, linkType);
};

/**
 * @method trackDownloadUsage
 * @description tracks the usage of a download feature
 * @param {string} fileName (file name) [name of the file downloaded]
 * @param {string} folderName (folder name) [optional, for nested folders and sub-folders use folder1/folder2/folder3]
 * @param {string} fileDownloadTitle (file download title) [optional]
 */
export const trackDownloadUsage = baseTrackDownload;
